import { jsPDF } from 'jspdf';
import 'jspdf-autotable/dist/jspdf.plugin.autotable.min.js';
import html2canvas from 'html2canvas';

export const generateKeywordPDF = async (keywordData, scanData, selectedKeyword, dateRange, mapElement) => {
  const doc = new jsPDF();
  
  // Ensure autoTable is available
  if (!doc.autoTable) {
    console.error('jsPDF autoTable plugin not loaded');
    return doc;
  }
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 25;
  const contentWidth = pageWidth - (margin * 2);
  
  // Color scheme matching GridRank branding
  const colors = {
    primary: [99, 102, 241],      // Purple/Indigo 
    secondary: [34, 197, 94],     // Green
    warning: [251, 146, 60],      // Orange
    danger: [239, 68, 68],        // Red
    info: [59, 130, 246],         // Blue
    gray: [156, 163, 175],        // Medium gray
    lightGray: [249, 250, 251],   // Very light gray
    darkGray: [55, 65, 81],       // Dark gray text
    borderGray: [229, 231, 235],  // Light border
    white: [255, 255, 255],
    purple: [139, 92, 246]        // Purple for header
  };
  
  let yPosition = 25;
  
  // HEADER SECTION - Keyword-specific design
  // Draw target/bullseye logo
  doc.setDrawColor(...colors.danger);
  doc.setFillColor(...colors.white);
  doc.setLineWidth(2);
  doc.circle(margin, yPosition, 4);
  doc.setFillColor(...colors.danger);
  doc.circle(margin, yPosition, 2, 'F');
  
  // Main title
  doc.setFontSize(26);
  doc.setTextColor(...colors.purple);
  doc.setFont(undefined, 'bold');
  doc.text('GridRank', margin + 12, yPosition + 2);
  
  yPosition += 15;
  doc.setFontSize(18);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(...colors.darkGray);
  doc.text('Keyword Performance Report', margin, yPosition);
  
  yPosition += 10;
  doc.setFontSize(14);
  doc.setTextColor(...colors.purple);
  doc.setFont(undefined, 'bold');
  doc.text(`"${selectedKeyword.name}"`, margin, yPosition);
  
  // Business info and date range
  yPosition += 12;
  doc.setDrawColor(...colors.purple);
  doc.setLineWidth(1);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  
  yPosition += 10;
  const businessName = scanData.businessName || scanData.business_name || 'Business Name';
  const reportDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  // Business name on its own line
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Business: ', margin, yPosition);
  doc.setFont(undefined, 'normal');
  doc.text(businessName, margin + 22, yPosition);
  
  yPosition += 7;
  
  // Date range and generated date on the next line
  doc.setFont(undefined, 'bold');
  doc.text('Date Range: ', margin, yPosition);
  doc.setFont(undefined, 'normal');
  doc.text(dateRange, margin + 28, yPosition);
  
  doc.setFont(undefined, 'bold');
  doc.text('Generated: ', pageWidth - margin - 65, yPosition);
  doc.setFont(undefined, 'normal');
  doc.text(reportDate, pageWidth - margin - 40, yPosition);
  
  // Calculate statistics from keywordData
  const processItems = keywordData.processItems || [];
  const totalGridPoints = processItems.length;
  
  const rankedItems = processItems.filter(item => item.rank && item.rank > 0);
  const avgRank = rankedItems.length > 0 
    ? rankedItems.reduce((acc, item) => acc + item.rank, 0) / rankedItems.length 
    : 0;
  
  const top3Count = processItems.filter(item => item.rank > 0 && item.rank <= 3).length;
  const top10Count = processItems.filter(item => item.rank > 0 && item.rank <= 10).length;
  const top20Count = processItems.filter(item => item.rank > 0 && item.rank <= 20).length;
  const notRankedCount = processItems.filter(item => !item.rank || item.rank === 0 || item.rank > 20).length;
  
  const gridCoverage = totalGridPoints > 0 
    ? ((rankedItems.length / totalGridPoints) * 100).toFixed(1) 
    : 0;
  
  // PERFORMANCE METRICS SECTION
  yPosition += 20;
  
  // Metrics cards - 3 per row
  const metricsData = [
    {
      label: 'AVG. POSITION',
      value: avgRank > 0 ? avgRank.toFixed(1) : 'N/A',
      color: colors.purple
    },
    {
      label: 'BEST POSITION',
      value: keywordData.bestPosition || 'N/A',
      color: colors.secondary
    },
    {
      label: 'GRID COVERAGE',
      value: `${gridCoverage}%`,
      color: colors.info
    },
    {
      label: 'TOP 3 RANKINGS',
      value: top3Count.toString(),
      color: colors.secondary
    },
    {
      label: 'TOP 10 RANKINGS',
      value: top10Count.toString(),
      color: colors.warning
    },
    {
      label: 'TOTAL GRID POINTS',
      value: totalGridPoints.toString(),
      color: colors.purple
    }
  ];
  
  const cardWidth = (contentWidth - 20) / 3;
  const cardHeight = 40;
  const cardSpacing = 10;
  
  metricsData.forEach((metric, index) => {
    const row = Math.floor(index / 3);
    const col = index % 3;
    const xPos = margin + (col * (cardWidth + cardSpacing));
    const yPos = yPosition + (row * (cardHeight + cardSpacing));
    
    // Card background
    doc.setFillColor(...colors.white);
    doc.rect(xPos, yPos, cardWidth, cardHeight, 'F');
    
    // Card border
    doc.setDrawColor(...metric.color);
    doc.setLineWidth(0.5);
    doc.roundedRect(xPos, yPos, cardWidth, cardHeight, 2, 2);
    
    // Metric value
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...metric.color);
    doc.text(metric.value, xPos + cardWidth/2, yPos + 20, { align: 'center' });
    
    // Metric label
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.gray);
    doc.text(metric.label, xPos + cardWidth/2, yPos + 32, { align: 'center' });
  });
  
  yPosition += (cardHeight * 2) + cardSpacing + 20;
  
  // Start Position Breakdown on a new page if needed
  if (yPosition > pageHeight - 150) {
    doc.addPage();
    yPosition = margin;
  }
  
  // POSITION BREAKDOWN SECTION
  doc.setFillColor(...colors.gray);
  doc.rect(margin, yPosition, 1.5, 5, 'F');
  doc.rect(margin + 2.5, yPosition - 2, 1.5, 7, 'F');
  doc.rect(margin + 5, yPosition - 1, 1.5, 6, 'F');
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Position Performance Breakdown', margin + 10, yPosition + 2);
  
  yPosition += 20;
  
  // Position cards
  const positionCards = [
    {
      value: top3Count,
      label: 'POSITIONS 1-3',
      borderColor: colors.secondary
    },
    {
      value: top10Count - top3Count,
      label: 'POSITIONS 4-7',
      borderColor: colors.purple
    },
    {
      value: top20Count - top10Count,
      label: 'POSITIONS 8-15',
      borderColor: colors.warning
    },
    {
      value: rankedItems.length - top20Count,
      label: 'POSITION 16+',
      borderColor: colors.danger
    },
    {
      value: notRankedCount,
      label: 'NOT RANKED',
      borderColor: [200, 200, 200]
    }
  ];
  
  const posCardWidth = (contentWidth - 15) / 2;
  const posCardHeight = 45;
  const posCardSpacing = 15;
  
  positionCards.forEach((card, index) => {
    const row = Math.floor(index / 2);
    const col = index % 2;
    const isLastCard = index === positionCards.length - 1;
    const isAloneInRow = isLastCard && (positionCards.length % 2 === 1);
    
    let xPos, cardWidth;
    if (isAloneInRow) {
      cardWidth = contentWidth;
      xPos = margin;
    } else {
      cardWidth = posCardWidth;
      xPos = margin + (col * (cardWidth + posCardSpacing));
    }
    const yPos = yPosition + (row * (posCardHeight + 15));
    
    // White background
    doc.setFillColor(...colors.white);
    doc.rect(xPos, yPos, cardWidth, posCardHeight, 'F');
    
    // Colored border
    doc.setDrawColor(...card.borderColor);
    doc.setLineWidth(0.5);
    doc.roundedRect(xPos, yPos, cardWidth, posCardHeight, 3, 3);
    
    // Value
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...card.borderColor);
    doc.text(card.value.toString(), xPos + cardWidth/2, yPos + 25, { align: 'center' });
    
    // Label
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.darkGray);
    doc.text(card.label, xPos + cardWidth/2, yPos + 37, { align: 'center' });
  });
  
  const totalRows = Math.ceil(positionCards.length / 2);
  yPosition += (posCardHeight * totalRows) + (15 * (totalRows - 1)) + 20;
  
  // Check if we need a new page for the heatmap
  if (yPosition > pageHeight - 120) {
    doc.addPage();
    yPosition = margin;
  }
  
  // KEYWORD HEATMAP SECTION
  if (mapElement) {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.darkGray);
    doc.text('Geographic Performance Heatmap', pageWidth/2, yPosition, { align: 'center' });
    yPosition += 15;
    
    try {
      // Find the actual map container with the heatmap
      let mapContainer = mapElement;
      
      // Try to find the wrapper that contains both the map and overlays
      if (mapElement && mapElement.parentElement) {
        // Check if parent is the keyword-map-wrapper
        if (mapElement.parentElement.className && mapElement.parentElement.className.includes('keyword-map-wrapper')) {
          mapContainer = mapElement.parentElement;
        }
      }
      
      // Ensure the map is visible and rendered
      if (mapContainer) {
        // Wait a brief moment for final rendering
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Try to capture the map with better settings
        const canvas = await html2canvas(mapContainer, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          imageTimeout: 15000, // Increase timeout for loading images
          width: mapContainer.offsetWidth,
          height: mapContainer.offsetHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: document.documentElement.offsetWidth,
          windowHeight: document.documentElement.offsetHeight,
          onclone: (clonedDoc) => {
            // Make sure Google Maps tiles are visible in the clone
            const clonedElement = clonedDoc.querySelector('.keyword-heatmap');
            if (clonedElement) {
              clonedElement.style.position = 'relative';
            }
          }
        });
        
        const imgData = canvas.toDataURL('image/png');
        
        // Calculate image dimensions to fit on page
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const maxHeight = 100;
        
        let finalWidth = imgWidth;
        let finalHeight = imgHeight;
        
        if (imgHeight > maxHeight) {
          finalHeight = maxHeight;
          finalWidth = (canvas.width * finalHeight) / canvas.height;
        }
        
        // Center the image
        const xOffset = margin + (contentWidth - finalWidth) / 2;
        
        // Add the image to PDF
        doc.addImage(imgData, 'PNG', xOffset, yPosition, finalWidth, finalHeight);
        yPosition += finalHeight + 15;
      } else {
        throw new Error('Map container not found');
      }
      
    } catch (error) {
      console.log('Could not capture heatmap:', error);
      // Add placeholder text if map capture fails
      doc.setFontSize(10);
      doc.setTextColor(...colors.gray);
      doc.text('Heatmap visualization not available', pageWidth/2, yPosition + 30, { align: 'center' });
      doc.text('(Ensure the keyword is expanded to view the heatmap)', pageWidth/2, yPosition + 40, { align: 'center' });
      yPosition += 60;
    }
  } else {
    // No map element provided
    doc.setFontSize(10);
    doc.setTextColor(...colors.gray);
    doc.text('Heatmap not available - expand keyword to generate', pageWidth/2, yPosition + 20, { align: 'center' });
    yPosition += 40;
  }
  
  // COMPETITOR ANALYSIS SECTION (if data available)
  if (keywordData.competitors && keywordData.competitors.length > 0) {
    // Check if we need a new page
    if (yPosition > pageHeight - 100) {
      doc.addPage();
      yPosition = margin;
    }
    
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...colors.darkGray);
    doc.text('Competitor Analysis', margin, yPosition);
    yPosition += 15;
    
    const competitorData = keywordData.competitors.slice(0, 10).map(comp => [
      comp.name || 'Unknown',
      comp.rank ? `#${comp.rank}` : 'Not Ranked',
      comp.duration || 'N/A',
      comp.reviews || 'N/A'
    ]);
    
    doc.autoTable({
      startY: yPosition,
      head: [['Competitor', 'Rank', 'Consistency', 'Reviews']],
      body: competitorData,
      theme: 'striped',
      headStyles: {
        fillColor: colors.purple,
        textColor: colors.white,
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: colors.darkGray
      },
      alternateRowStyles: {
        fillColor: colors.lightGray
      },
      margin: { left: margin, right: margin },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 35, halign: 'center' },
        3: { cellWidth: 30, halign: 'center' }
      }
    });
    
    yPosition = doc.autoTable.previous.finalY + 20;
  }
  
  // DETAILED GRID DATA TABLE - New page
  doc.addPage();
  yPosition = margin;
  
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...colors.darkGray);
  doc.text('Detailed Grid Performance Data', pageWidth/2, yPosition, { align: 'center' });
  
  yPosition += 15;
  
  // Prepare table data (without distance)
  const tableHeaders = ['Grid Point', 'Rank', 'Status', 'Latitude', 'Longitude'];
  const tableRows = processItems.slice(0, 50).map((item, index) => {
    const rank = item.rank === 0 ? '20+' : (item.rank || 'Not Ranked');
    return [
      `Point ${item.grid_index || index + 1}`,
      rank.toString(),
      item.status || 'completed',
      item.latitude ? parseFloat(item.latitude).toFixed(6) : '',
      item.longitude ? parseFloat(item.longitude).toFixed(6) : ''
    ];
  });
  
  doc.autoTable({
    startY: yPosition,
    head: [tableHeaders],
    body: tableRows,
    theme: 'grid',
    headStyles: {
      fillColor: colors.purple,
      textColor: colors.white,
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: colors.darkGray
    },
    alternateRowStyles: {
      fillColor: colors.lightGray
    },
    margin: { left: margin, right: margin },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 30, halign: 'center' },
      2: { cellWidth: 35, halign: 'center' },
      3: { cellWidth: 35 },
      4: { cellWidth: 35 }
    }
  });
  
  // Footer on all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Footer line
    doc.setDrawColor(...colors.borderGray);
    doc.setLineWidth(0.5);
    doc.line(margin, pageHeight - 30, pageWidth - margin, pageHeight - 30);
    
    // Footer text
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...colors.gray);
    doc.text('© GridRank - Keyword Performance Report', pageWidth/2, pageHeight - 20, { align: 'center' });
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 20, { align: 'right' });
    doc.text(new Date().toLocaleString(), margin, pageHeight - 20);
  }
  
  return doc;
};